/**
 * Capture feature — API surface.
 *
 * Two halves:
 *
 *   1. Re-exports the chat-API primitives the Capture grid composes
 *      (the grid is in part a thin layer over chat conversations).
 *      Importing from "@/features/capture/api" gives callers one
 *      package to reach.
 *
 *   2. Capture-specific endpoints — POST /capture, the preview, the receipt,
 *      the override, the feed, the SSE stream, the stream lifecycle.
 *      All mirror the Pydantic shapes in backend/src/capture/schemas.py.
 */
import { apiClient } from "@/shared/lib/api/client";

import type {
  AcceptTypeRequest,
  CaptureFeedItem,
  CaptureMode,
  CapturePreview,
  CapturePreviewRequest,
  CaptureReceipt,
  CaptureRequest,
  RoutedKind,
  StreamResponse,
} from "../types";

// ====================================================================
// Re-exports from chat (the grid composes existing chat primitives)
// ====================================================================
export {
  abortAgentRun,
  chatKeys,
  fetchActiveRuns,
  isOptimisticConversationId,
  kickoffAgentRun,
  mintOptimisticConversationId,
  resumeRunEvents,
  useActiveRuns,
  useConversation,
  useConversationHistory,
  useConversations,
  useCreateConversation,
  useDeleteConversation,
  useUpdateConversation,
} from "@/features/chat/api";

// ====================================================================
// Capture-specific endpoints
// ====================================================================

/** TanStack query-key factory for capture reads. */
export const captureKeys = {
  all: ["capture"] as const,
  receipt: (id: string) => ["capture", "receipt", id] as const,
  preview: (req: CapturePreviewRequest) =>
    ["capture", "preview", req.text, req.mode ?? null, req.input_type_id ?? null] as const,
  feed: (params?: {
    routed_kind?: RoutedKind;
    lifecycle?: string;
    stream_id?: string;
    skip?: number;
    limit?: number;
  }) => ["capture", "feed", params] as const,
};

/** POST /capture — the one way text becomes a task, entry, note or rule. Answers a receipt. */
export async function fireCapture(body: CaptureRequest): Promise<CaptureReceipt> {
  return apiClient<CaptureReceipt>("/api/capture", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** GET /capture/{id} — the receipt again, polled until it settles. */
export async function getCaptureReceipt(id: string): Promise<CaptureReceipt> {
  return apiClient<CaptureReceipt>(`/api/capture/${encodeURIComponent(id)}`);
}

/** POST /capture/{id}/resolve — file a needs-attention capture under an existing type. */
export async function resolveCapture(id: string, inputTypeId: string): Promise<CaptureReceipt> {
  return apiClient<CaptureReceipt>(`/api/capture/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    body: JSON.stringify({ input_type_id: inputTypeId }),
  });
}

/** POST /capture/{id}/accept-type — create the proposed type, then the entry. */
export async function acceptCaptureType(id: string, body: AcceptTypeRequest): Promise<CaptureReceipt> {
  return apiClient<CaptureReceipt>(`/api/capture/${encodeURIComponent(id)}/accept-type`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** POST /capture/preview — what Enter would create, without writing anything. */
export async function previewCapture(
  body: CapturePreviewRequest,
  signal?: AbortSignal,
): Promise<CapturePreview> {
  return apiClient<CapturePreview>("/api/capture/preview", {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
}

/** PATCH /capture/{id}/override — user redirects a needs_attention card. */
export async function overrideCapture(
  captureId: string,
  mode: CaptureMode,
): Promise<CaptureFeedItem> {
  return apiClient<CaptureFeedItem>(
    `/api/capture/${encodeURIComponent(captureId)}/override`,
    {
      method: "PATCH",
      body: JSON.stringify({ mode }),
    },
  );
}

/** GET /capture/feed — owner-scoped paginated card list. */
export async function fetchCaptureFeed(params?: {
  routed_kind?: RoutedKind;
  lifecycle?: string;
  stream_id?: string;
  skip?: number;
  limit?: number;
}): Promise<{
  items: CaptureFeedItem[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}> {
  const qs = new URLSearchParams();
  if (params?.routed_kind) qs.set("routed_kind", params.routed_kind);
  if (params?.lifecycle) qs.set("lifecycle", params.lifecycle);
  if (params?.stream_id) qs.set("stream_id", params.stream_id);
  if (params?.skip != null) qs.set("skip", String(params.skip));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiClient(`/api/capture/feed${suffix}`);
}

/** POST /capture/streams/start — open a new stream parent note. */
export async function startStream(title?: string | null): Promise<StreamResponse> {
  return apiClient<StreamResponse>("/api/capture/streams/start", {
    method: "POST",
    body: JSON.stringify({ title: title ?? null }),
  });
}

/** POST /capture/streams/{id}/end — close + auto-title. */
export async function endStream(
  streamId: string,
  opts?: { generate_title?: boolean },
): Promise<StreamResponse> {
  return apiClient<StreamResponse>(
    `/api/capture/streams/${encodeURIComponent(streamId)}/end`,
    {
      method: "POST",
      body: JSON.stringify({
        generate_title: opts?.generate_title ?? true,
      }),
    },
  );
}
